/**
 * FS-502 — which fields an operator may correct on each document, and what
 * each one is called on screen.
 *
 * The lists are the server's editable-field lists, in the order an operator
 * reads a nameplate: what the unit is, then what it is rated at, then how the
 * record is classified. A field the server does not accept is not offered,
 * because the only thing sending one achieves is a refusal.
 */

import { motorTypeLabel } from '../../utils/equipmentQuality';

export type EquipmentFieldKind =
  | 'text'
  | 'number'
  | 'date'
  | 'boolean'
  | 'enum'
  /** A set of names, written as an array. Plan 123's `absentComponents`. */
  | 'components';

export interface EquipmentFieldSpec {
  /** The document field name, sent verbatim. */
  name: string;
  label: string;
  kind: EquipmentFieldKind;
  /**
   * Which vocabulary applies. `motor_types`, `equipment_sides` and
   * `capacity_sources` come from the GET's `options`; `absent_components` is
   * fixed in the first release (§4.1) and is held here.
   */
  options?:
    | 'motor_types'
    | 'equipment_sides'
    | 'capacity_sources'
    | 'absent_components'
    | 'filter_types';
  /** Shown under the field as a plain sentence when the name is not self-explanatory. */
  help?: string;
  /**
   * False when the server refuses to clear the value once it is set, so the
   * select offers no "clear" choice. Default true.
   */
  clearable?: boolean;
}

/**
 * Plan 123 §4.1 — the parts a system can be declared not to have.
 *
 * Only the two whole machines. A filter is per Clip, and a system-wide "no
 * filter" would erase a sibling's need; a package unit cannot have half of
 * itself declared absent. The server rejects both, so neither is offered.
 */
export const ABSENT_COMPONENT_OPTIONS = ['outdoor_unit', 'indoor_unit'];

/**
 * Plan 123 Phase 4b: "no indoor unit" did not say which machine, and both
 * labels read as a value rather than as a statement about the home. They are
 * read twice — as a tick box, and after "on file:" under it — so each has to
 * work in both places.
 */
const ABSENT_COMPONENT_LABELS: Record<string, string> = {
  outdoor_unit: 'no outdoor unit on this system',
  indoor_unit: 'no furnace or air handler on this system',
};

const NAME: EquipmentFieldSpec = { name: 'name', label: 'System name', kind: 'text' };
const MANUFACTURER: EquipmentFieldSpec = { name: 'manufacturer', label: 'Brand', kind: 'text' };
const MODEL: EquipmentFieldSpec = { name: 'modelNumber', label: 'Model number', kind: 'text' };
const SERIAL: EquipmentFieldSpec = { name: 'serialNumber', label: 'Serial number', kind: 'text' };
const TONNAGE: EquipmentFieldSpec = {
  name: 'tonnage',
  label: 'Tonnage',
  kind: 'number',
  help: 'Cooling capacity in tons, as the nameplate states it.',
};
const SEER: EquipmentFieldSpec = { name: 'seerRating', label: 'SEER', kind: 'number' };
const SEER2: EquipmentFieldSpec = { name: 'seer2Rating', label: 'SEER2', kind: 'number' };
const SIDE: EquipmentFieldSpec = {
  name: 'equipmentSide',
  label: 'Part of the system',
  kind: 'enum',
  options: 'equipment_sides',
  help: 'Which piece of equipment this record describes.',
};
const CAPACITY_SOURCE: EquipmentFieldSpec = {
  name: 'capacitySource',
  label: 'How the capacity was determined',
  kind: 'enum',
  options: 'capacity_sources',
};

/**
 * Plan 123 §4.1 — what this system does not have.
 *
 * It sits on the HVAC record because it is a fact about the equipment record,
 * not about one Clip, and it retires a component from the Missing tab instead
 * of leaving it as a gap nobody can ever close. Ticking nothing and saving
 * clears the declaration.
 */
export const ABSENT_COMPONENTS: EquipmentFieldSpec = {
  name: 'absentComponents',
  label: 'Parts this system does not have',
  kind: 'components',
  options: 'absent_components',
  help: 'Tick a part only when the home really has no such machine. Nobody is asked for a photo of it after that, and it stops counting as missing.',
};

export const HVAC_FIELDS: EquipmentFieldSpec[] = [
  NAME,
  MANUFACTURER,
  MODEL,
  SERIAL,
  TONNAGE,
  { name: 'btuRating', label: 'BTU rating', kind: 'number' },
  {
    name: 'blowerTypeNameplate',
    label: 'Motor type',
    kind: 'enum',
    options: 'motor_types',
    help: 'The blower motor family printed on the nameplate. It decides how airflow is calculated, so leave it alone unless the label says otherwise.',
  },
  SIDE,
  CAPACITY_SOURCE,
  { name: 'fuelType', label: 'Fuel type', kind: 'text' },
  SEER,
  SEER2,
  ABSENT_COMPONENTS,
];

export const AC_FIELDS: EquipmentFieldSpec[] = [
  NAME,
  MANUFACTURER,
  MODEL,
  SERIAL,
  TONNAGE,
  SEER,
  SEER2,
  { name: 'compressorStages', label: 'Compressor stages', kind: 'text' },
  SIDE,
  CAPACITY_SOURCE,
];

/**
 * Plan 120 — the evaporator coil.
 *
 * What a coil HAS: an identity, a capacity, and when it was made. What a coil
 * does NOT have, and what is therefore not offered here: a blower motor, a BTU
 * rating, a fuel type, an efficiency rating, a SEER figure and a compressor.
 * Those belong to the furnace or air handler and to the outdoor unit, and the
 * server refuses them on a coil target — offering a box for one would only
 * produce a refusal.
 *
 * `equipmentSide` is not offered either: a coil record's side is the constant
 * `indoor_coil`, and a box that can only hold one value is a trap.
 */
export const COIL_FIELDS: EquipmentFieldSpec[] = [
  MANUFACTURER,
  MODEL,
  TONNAGE,
  // FS-783 addendum 2 F11: a converted coil tonnage is stored with
  // `capacitySource: "derived"`, and the two are corrected together, so the
  // coil offers the box the server requires alongside tonnage.
  CAPACITY_SOURCE,
  SERIAL,
  { name: 'yearMade', label: 'Year made', kind: 'text' },
  {
    name: 'refrigerantType',
    label: 'Refrigerant',
    kind: 'text',
    help: 'The refrigerant the coil is charged for, as the label states it (R-410A, R-454B).',
  },
];

/**
 * FS-593 — the two slot kinds the app writes as `filterType`, and the words
 * for them. The server takes exactly these and will not clear the field.
 */
export const FILTER_TYPE_OPTIONS = ['airReturns', 'furnace'];
const FILTER_TYPE_LABELS: Record<string, string> = {
  airReturns: 'Return grille',
  furnace: 'Furnace',
};

/**
 * FS-593 — the homeowner's name for a filter spot. FS-825: worded as a filter
 * name here, for a record with no type; `filterLocationField` re-words it for
 * the type chosen.
 */
const FILTER_LOCATION_FIELD: EquipmentFieldSpec = {
  name: 'location',
  label: 'Filter name',
  kind: 'text',
  help: 'Where this filter is, for example Upstairs Loft or Basement.',
};

/**
 * FS-593 — a filter slot's kind and the homeowner's name for it. These are
 * the only two fields that can be set on a filter record that is not the
 * Clip's own, so they are a list of their own as well as the head of the
 * own filter's list.
 */
export const FILTER_SLOT_FIELDS: EquipmentFieldSpec[] = [
  {
    name: 'filterType',
    label: 'Filter type',
    kind: 'enum',
    options: 'filter_types',
    clearable: false,
    help: 'Return grille: a filter behind a grille in a room. Furnace: the whole-house filter at the furnace.',
  },
  FILTER_LOCATION_FIELD,
];

/**
 * FS-825 — the filter's name box is called after the filter type chosen in
 * the same card, because "Name" alone was read as the home's name
 * (2026-10-05). A return grille is named for its room, a whole-house filter
 * for where the furnace or air handler is. Pure, so the card can re-word the
 * box on every change of the type select.
 */
export function filterLocationField(filterType: unknown): EquipmentFieldSpec {
  if (filterType === 'airReturns') {
    return {
      ...FILTER_LOCATION_FIELD,
      label: 'Grille name',
      help: 'The room the grille is in, for example Upstairs Loft.',
    };
  }
  if (filterType === 'furnace') {
    return {
      ...FILTER_LOCATION_FIELD,
      label: 'Filter name',
      help: 'Where the furnace or air handler is, for example Basement.',
    };
  }
  return FILTER_LOCATION_FIELD;
}

/** `specs` with the name box worded for `filterType`; every other field as it is. */
export const withFilterLocationWording = (
  specs: EquipmentFieldSpec[],
  filterType: unknown,
): EquipmentFieldSpec[] =>
  specs.map((spec) => (spec.name === FILTER_LOCATION_FIELD.name ? filterLocationField(filterType) : spec));

/**
 * FS-783: the brand, rating and size are refused as a clear by the server
 * (the app cannot read a filter without them), so they are marked here too.
 * A text box offers no "clear" choice either way; the mark is what tells the
 * removed-photo clear step to leave them for the operator to retype.
 */
export const FILTER_FIELDS: EquipmentFieldSpec[] = [
  { ...MANUFACTURER, clearable: false },
  {
    name: 'rating',
    label: 'Rating',
    kind: 'text',
    clearable: false,
    help: 'The MERV, MPR or FPR figure printed on the filter.',
  },
  { name: 'length', label: 'Length (in.)', kind: 'number', clearable: false },
  { name: 'width', label: 'Width (in.)', kind: 'number', clearable: false },
  { name: 'thickness', label: 'Thickness (in.)', kind: 'number', clearable: false },
  MODEL,
  { name: 'installDate', label: 'Install date', kind: 'date' },
  { name: 'isNewFilter', label: 'Installed new', kind: 'boolean' },
];

/**
 * `equipmentSide` in words. The console has no map for these yet — the quality
 * ladder names the states, not the sides — so it lives here, and an
 * unrecognized value is shown as stored rather than hidden.
 */
const EQUIPMENT_SIDE_LABELS: Record<string, string> = {
  outdoor_condenser: 'outdoor condenser',
  indoor_air_handler: 'indoor air handler',
  indoor_coil: 'indoor coil',
  package_unit: 'package unit',
};

/** `capacitySource` in words, same rule. */
const CAPACITY_SOURCE_LABELS: Record<string, string> = {
  nameplate: 'nameplate',
  model_decode: 'model number',
  coil_assumed: 'assumed from the coil',
  user: 'entered by a person',
  derived: 'calculation',
};

const humanize = (value: string) => value.replace(/_/g, ' ');

/** The words for one member of a vocabulary the GET sent. */
export function optionLabel(
  vocabulary: EquipmentFieldSpec['options'],
  value: string,
): string {
  if (vocabulary === 'motor_types') return motorTypeLabel(value);
  if (vocabulary === 'equipment_sides') return EQUIPMENT_SIDE_LABELS[value] ?? humanize(value);
  if (vocabulary === 'capacity_sources') return CAPACITY_SOURCE_LABELS[value] ?? humanize(value);
  if (vocabulary === 'absent_components') {
    return ABSENT_COMPONENT_LABELS[value] ?? humanize(value);
  }
  if (vocabulary === 'filter_types') return FILTER_TYPE_LABELS[value] ?? value;
  return humanize(value);
}

/**
 * A stored value as an operator reads it. Enum fields borrow their vocabulary's
 * words so the value under the box and the option inside it read the same; an
 * absent value says so rather than leaving a blank.
 */
export function displayValue(spec: EquipmentFieldSpec, value: unknown): string {
  if (spec.kind === 'components') {
    const names = Array.isArray(value) ? value.map(String) : [];
    if (names.length === 0) return 'nothing declared absent';
    return names.map((name) => optionLabel(spec.options, name)).join(', ');
  }
  if (value === null || value === undefined || value === '') return 'not on file';
  if (spec.kind === 'boolean') return value ? 'yes' : 'no';
  if (spec.kind === 'enum') return optionLabel(spec.options, String(value));
  return String(value);
}

/** The same, for a value that came back inside a diff rather than off a document. */
export function changeValue(spec: EquipmentFieldSpec | undefined, value: unknown): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return 'cleared';
    return spec ? displayValue(spec, value) : value.join(', ');
  }
  if (value === null || value === undefined || value === '') return 'cleared';
  if (!spec) return String(value);
  return displayValue(spec, value);
}

/**
 * What goes in the box for a stored value. A set of names is held as a
 * comma-joined string while it is being edited, so every field the card
 * tracks is still one string.
 */
export function editableValue(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(',');
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
}

/** The names ticked in a `components` box right now. */
export const selectedComponents = (raw: string): string[] =>
  raw.split(',').map((part) => part.trim()).filter((part) => part.length > 0);

export type EquipmentEditKind = 'hvac' | 'ac' | 'filter' | 'coil';

export const fieldsFor = (kind: EquipmentEditKind): EquipmentFieldSpec[] => {
  if (kind === 'hvac') return HVAC_FIELDS;
  if (kind === 'ac') return AC_FIELDS;
  if (kind === 'coil') return COIL_FIELDS;
  return FILTER_FIELDS;
};

export const specFor = (kind: EquipmentEditKind, name: string) =>
  fieldsFor(kind).find((f) => f.name === name) ??
  (kind === 'filter' ? FILTER_SLOT_FIELDS.find((f) => f.name === name) : undefined);

/**
 * FS-825 — `specFor`, with a filter's name box worded for `filterType` the
 * way the box itself is, for the places that name a field away from its box
 * (the preview table, the "record changed" notice).
 */
export const specForFilterType = (kind: EquipmentEditKind, name: string, filterType: unknown) => {
  const spec = specFor(kind, name);
  return kind === 'filter' && spec?.name === FILTER_LOCATION_FIELD.name ? filterLocationField(filterType) : spec;
};
